import { setMinerFlagRequest } from "@services/changeMinerSettings";
import { useAuth } from "@utils/hooks/useAuth";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import useTypedRequest from "@utils/hooks/useTypedRequest";
import { makeNotifs } from "@utils/makeNotifs";
import { ButtonProps } from "antd";
import { useEffect, useRef, useState } from "react";
import { IMinerFlagProps } from "./MinerFlag.props";

export type FlagColorType =
  | "green"
  | "yellow"
  | "red"
  | "black"
  | "violet"
  | "orange";

type FlagMetaType = {
  flagText: string;
  flagColor: FlagColorType;
};

function getFlagMeta(meta: IMinerFlagProps["meta"]): FlagMetaType {
  return {
    flagText: meta?.flagText || "",
    flagColor: (meta?.flagColor || "black") as FlagColorType,
  };
}

export const useMinerFlagControl = (props: IMinerFlagProps) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const { ok } = makeNotifs();
  const { onSuccess } = useGlobalNotifs();
  const auth = useAuth();
  const initialMeta = getFlagMeta(props.meta);

  const [initialFlagMeta, setInitialFlagMeta] = useState<FlagMetaType>(
    initialMeta
  );
  const [flagMeta, setSetFlagMeta] = useState<FlagMetaType>(
    initialMeta
  );

  useEffect(() => {
    const metaFlag = getFlagMeta(props.meta);
    setSetFlagMeta(metaFlag);
    setInitialFlagMeta(metaFlag);
  }, [props.meta?.flagText, props.meta?.flagColor, auth.isAdmin]);

  const [color, setColor] = useState<FlagColorType>(flagMeta.flagColor);
  //const [savedColor, setSavedColor] = useState<FlagColorType>(flagMeta.flagColor);
  const [text, setText] = useState<string>(flagMeta.flagText);
  //const [savedText, setSavedText] = useState<string>(flagMeta.flagText);

  //close tooltip
  const [open, setOpen] = useState<boolean | undefined>(undefined);

  const { request, loading } = useTypedRequest(setMinerFlagRequest);

  useEffect(() => {
    setText(flagMeta.flagText);
    //setSavedText(flagMeta.flagText);
    setColor(flagMeta.flagColor);
    //setSavedColor(flagMeta.flagColor);
  }, [flagMeta]);

  const onSave = async () => {
    const meta = props.meta ? { ...props.meta } : {};
    const col = color === "black" ? "red" : color;
    const flag = { flagText: text, flagColor: col };
    const payload = auth.isAdmin ? { ...meta, ...flag } : flag;
    const res = await request(props.minerId, payload);
    if (!res) return;
    onSuccess(ok.success);
    props.optionalFunc?.(JSON.stringify(payload));
    setInitialFlagMeta({ flagText: text, flagColor: col });
    // setSavedText(text);
    // setSavedColor(col);
    setColor(col);
    //close
    setOpen(false);
    setTimeout(() => {
      setOpen(undefined);
    });
  };

  const onSaveHandler: ButtonProps["onClick"] = (e) => {
    e.stopPropagation();
    onSave();
  };

  const afterOpenChanged = (toggle: boolean) => {
    props.afterOpen?.(toggle);

    if (toggle) {
      //setText(savedText);
      //setColor(savedColor === "black" ? "red" : savedColor);
      ref.current?.focus();
    } else {
      //setText("");
      //setColor("black");
    }
  };

  const onChangeColor = (c: FlagColorType) => {
    setColor(c);
    ref.current?.focus();
  };

  const onCancel = () => {
    setText(initialFlagMeta.flagText);
    setColor(initialFlagMeta.flagColor);
    // setSavedText(initialFlagMeta.flagText);
    // setSavedColor(initialFlagMeta.flagColor);
    ref.current?.blur();
    setOpen(false);
    setTimeout(() => {
      setOpen(undefined);
    });
  };

  const onDelete = async () => {
    const meta = props.meta ? { ...props.meta } : {};
    const payload = auth.isAdmin
      ? { ...meta, flagText: "", flagColor: "" }
      : {
        flagText: "",
        flagColor: "",
      };
    const res = await request(props.minerId, payload);
    if (!res) return;
    onSuccess(ok.success);

    props.optionalFunc?.(
      JSON.stringify({ ...props.meta, flagText: "", flagColor: "" })
    );
    setText("");
    //setSavedText("");
    setColor("black");
    //setSavedColor("black");
    setInitialFlagMeta({ flagText: "", flagColor: "black" });

    //close
    setOpen(false);
    setTimeout(() => {
      setOpen(undefined);
    });
  };

  const onSaveByKeydown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.ctrlKey && e.key === "Enter") {
      onSave();
    } else if (e.key === "Escape") {
      onCancel();
    }
  };

  return {
    ref,
    text,
    color,
    isActive: color !== "black" || text.length > 0,
    loading,
    open,

    setText,
    onSave: onSaveHandler,
    onDelete,
    afterOpenChanged,
    onChangeColor,
    onSaveByKeydown,
    onCancel,
  };
};
